import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from './Button';
import { cx } from '@/lib/format';
import { useFocusTrap } from '@/lib/useFocusTrap';

export function Dialog({
  open,
  onClose,
  title,
  children,
  width = 'max-w-lg',
  footer,
  bare = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: string;
  footer?: ReactNode;
  /** no header/padding; `title` is used as the accessible name only */
  bare?: boolean;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  // focus in / trap / restore runs once per open; `onClose` is read through a ref so parent re-renders
  // (every keystroke when the form state lives in the parent) never yank focus back to the first field (H2)
  useFocusTrap(ref, { active: open, onEscape: onClose });
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={bare ? undefined : titleId}
        aria-label={bare && typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cx(
          'relative w-full rounded-xl border border-line bg-bg-elev shadow-2xl outline-none',
          width,
        )}
      >
        {bare ? (
          children
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h2 id={titleId} className="text-sm font-semibold">
                {title}
              </h2>
              <IconButton label="Close" size="sm" onClick={onClose} data-close>
                <X size={16} />
              </IconButton>
            </div>
            <div className="max-h-[70vh] overflow-auto px-4 py-4 text-sm">{children}</div>
            {footer && (
              <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
